import { Button, ButtonProps } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import Filter, { FilterValue } from 'components/Filter'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import { TextLink } from 'components/Link'
import Paper from 'components/Paper'
import { TableHeader } from 'components/Table'
import { useAbility } from 'providers/casl'
import { ViewportPageContext, ViewportPageContextType } from '../../contexts/ViewportPageContext'
import { ViewportForm } from '../../forms/ViewportForm'
import { KNOWLEDGE_BASE_URL } from './constants'

export const ViewportPage = (): React.JSX.Element => {
  const { t } = useTranslation(['features/globalSetup/viewport', 'common'])
  const ability = useAbility()
  const [filters, setFilters] = useState<FilterValue[]>([])
  const [onAddNew, setOnAddNew] = useState<ViewportPageContextType['onAddNew']>(() => {})

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: 'global-setup-viewports-form',
  })
  const contextProps: ViewportPageContextType = {
    ...commonContextProps,
    filters,
    onAddNew,
    setOnAddNew,
  }

  const DescriptionLink: React.JSX.Element = (
    <TextLink
      link={{
        title: t('common:learnMore'),
        url: KNOWLEDGE_BASE_URL,
      }}
    />
  )

  const ViewportsFilter: React.JSX.Element = (
    <Filter
      id='viewports-filter'
      onChange={setFilters}
      placeholder={t('filter')}
      values={filters}
    />
  )

  const AddButton = ({ onClick }: { onClick: ButtonProps['onClick'] }): React.JSX.Element => (
    <Button
      data-cy='viewport-add'
      onClick={onClick}
      variant='contained'
    >
      {t('common:actions.addNew')}
    </Button>
  )

  const canCreateUpdateDelete: boolean =
    ability.can('create', 'GlobalSetupFeature-viewport') &&
    ability.can('update', 'GlobalSetupFeature-viewport') &&
    ability.can('delete', 'GlobalSetupFeature-viewport')

  return (
    <ViewportPageContext.Provider value={contextProps}>
      <ViewportPageContext.Consumer>
        {(ctxProps: ViewportPageContextType): React.JSX.Element => (
          <>
            {canCreateUpdateDelete && <PageHeader actions={<SaveButton {...ctxProps} />} />}

            <Paper>
              <TableHeader
                actions={
                  canCreateUpdateDelete ? <AddButton onClick={ctxProps.onAddNew} /> : undefined
                }
                description={
                  canCreateUpdateDelete
                    ? t('header.description.employee')
                    : t('header.description.viewer')
                }
                descriptionEndAdornment={DescriptionLink}
                filter={ViewportsFilter}
                title={t('header.title')}
              />

              <GapBox />

              <ViewportForm />
            </Paper>
          </>
        )}
      </ViewportPageContext.Consumer>
    </ViewportPageContext.Provider>
  )
}
